/**
 * How Home reads the server's answer to an action on one record.
 *
 * ONE DEFINITION, SHARED BY EVERY HOME POP-UP. Approvals and the work-section
 * detail pop-up both submit an action against one record and both have to tell
 * "your input was wrong" from "the list on screen is out of date". Those are the
 * same two questions, so the mapping lives here rather than being reimplemented
 * per pop-up and drifting.
 *
 * THE OUTCOMES ARE DISTINGUISHED BY STATUS AND `error_code`, NEVER BY THE
 * MESSAGE TEXT. The text is for the user and may be translated; the code is the
 * contract. Callers supply their own wording and read only the state from here.
 *
 * ONLY A VALIDATION FAILURE LEAVES THE USER SOMETHING TO CORRECT. Every other
 * terminal state means what is on screen is stale, so the caller refreshes
 * rather than letting the user act on it again — that is what `isStaleState`
 * answers.
 *
 * ON THE NAMES: `ACTION_NOT_APPROVER` and the two `CODE_*` values read as
 * approvals vocabulary because that is where they started, and they are kept
 * verbatim so lifting them changed no behaviour. What the state MEANS is
 * general — the server refused this actor — and it is correct for the work
 * pop-up too, where a 403 means the user does not hold the module's own manage
 * ability. Rename it only alongside its consumers, and not for tidiness.
 */

export const ACTION_IDLE = 'idle';
export const ACTION_SUBMITTING = 'submitting';
export const ACTION_DONE = 'done';
/** 409 APPROVAL_NOT_PENDING — someone else got there first, or a double submit. */
export const ACTION_COLLISION = 'collision';
/** 403 APPROVAL_NOT_AN_APPROVER — the server refused this actor. */
export const ACTION_NOT_APPROVER = 'not_approver';
/** 404 — the record was deleted, reassigned or unpinned since the list was read. */
export const ACTION_GONE = 'gone';
/**
 * 422 — correctable in place, and THE ONLY NON-TERMINAL REFUSAL.
 *
 * A caller must keep its submit control visible on this state. Hiding the
 * controls here tells the user what to fix and then takes away the button that
 * submits the fix, which is a dead end with no way back.
 */
export const ACTION_INVALID = 'invalid';
export const ACTION_ERROR = 'error';

export const CODE_NOT_PENDING = 'APPROVAL_NOT_PENDING';
export const CODE_NOT_AN_APPROVER = 'APPROVAL_NOT_AN_APPROVER';

/** The states that mean the list is stale rather than the input wrong. */
export const isStaleState = (state) => [ACTION_DONE, ACTION_COLLISION, ACTION_NOT_APPROVER, ACTION_GONE].includes(state);

export const stateFor = (error) => {
    const status = error?.response?.status;
    const code = error?.response?.data?.error_code;

    if (status === 409 || code === CODE_NOT_PENDING) {
        return ACTION_COLLISION;
    }

    if (status === 403 || code === CODE_NOT_AN_APPROVER) {
        return ACTION_NOT_APPROVER;
    }

    if (status === 404) {
        return ACTION_GONE;
    }

    if (status === 422) {
        return ACTION_INVALID;
    }

    return ACTION_ERROR;
};
